import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import {
  getSubscriptionInfo,
  cancelSubscription,
  Subscription,
  getProfile,
  saveProfile,
  UserProfile,
  SaveProfilePayload,
} from '../../utils/api/natureDrApi';
import './Settings.css';

const formatDate = (timestamp: number): string => {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const getCurrencySymbol = (currency: string): string => {
  switch (currency) {
    case 'usd': return '$';
    case 'eur': return '€';
    case 'gbp': return '£';
    default: return currency;
  }
};

const formatPrice = (amount: number | undefined, currency: string | undefined): string => {
  if (!amount || !currency) return 'Free';
  return `${getCurrencySymbol(currency)} ${(amount / 100).toFixed(2)}`;
};

const sexLabel = (val: string) => {
  if (val === 'prefer_not_to_say') return 'Prefer not to say';
  return val.charAt(0).toUpperCase() + val.slice(1);
};

const ETHNICITY_OPTIONS = [
  'White', 'Black / African / Caribbean', 'Asian', 'Mixed / Multiple',
  'Hispanic / Latino', 'Middle Eastern / North African', 'Other',
];

// ── Profile Section ──────────────────────────────────────────────
const ProfileSection: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Edit fields
  const [dob, setDob] = useState('');
  const [sex, setSex] = useState<'male' | 'female' | 'prefer_not_to_say'>('male');
  const [heightUnit, setHeightUnit] = useState<'cm' | 'feet'>('cm');
  const [heightCm, setHeightCm] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weightUnit, setWeightUnit] = useState<'kg' | 'stone' | 'lbs'>('kg');
  const [weightKg, setWeightKg] = useState('');
  const [weightStone, setWeightStone] = useState('');
  const [weightLbs, setWeightLbs] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [pregnant, setPregnant] = useState<boolean | undefined>(undefined);
  const [conditions, setConditions] = useState<string[]>([]);
  const [medications, setMedications] = useState<string[]>([]);
  const [condInput, setCondInput] = useState('');
  const [medInput, setMedInput] = useState('');

  const populateFields = (p: UserProfile) => {
    setDob(p.dateOfBirth);
    setSex(p.biologicalSex);
    setHeightCm(String(p.heightCm));
    setWeightKg(String(p.weightKg));
    setEthnicity(p.ethnicity || '');
    setPregnant(p.isPregnant);
    setConditions(p.conditions || []);
    setMedications(p.medications || []);
    // Reset imperial fields
    setHeightUnit('cm');
    setWeightUnit('kg');
    setHeightFeet('');
    setHeightInches('');
    setWeightStone('');
    setWeightLbs('');
  };

  useEffect(() => {
    (async () => {
      try {
        const { profile: p } = await getProfile();
        if (p) {
          setProfile(p);
          populateFields(p);
        }
      } catch {
        // Profile not found — that's ok
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const startEditing = () => {
    if (profile) populateFields(profile);
    setEditing(true);
    setError(null);
    setSuccess(false);
  };

  const cancelEditing = () => {
    if (profile) populateFields(profile);
    setEditing(false);
    setError(null);
  };

  const addTag = (list: string[], setList: (v: string[]) => void, input: string, setInput: (v: string) => void) => {
    const trimmed = input.trim();
    if (trimmed && !list.includes(trimmed)) setList([...list, trimmed]);
    setInput('');
  };

  const removeTag = (list: string[], setList: (v: string[]) => void, index: number) => {
    setList(list.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    const payload: SaveProfilePayload = {
      dateOfBirth: dob,
      biologicalSex: sex,
      heightUnit,
      weightUnit,
      ...(heightUnit === 'cm'
        ? { heightCm: Number(heightCm) }
        : { heightFeet: Number(heightFeet), heightInches: Number(heightInches) }),
      ...(weightUnit === 'kg'
        ? { weightKg: Number(weightKg) }
        : weightUnit === 'stone'
          ? { weightStone: Number(weightStone), weightLbs: Number(weightLbs) }
          : { weightLbs: Number(weightLbs) }),
      ...(ethnicity ? { ethnicity } : {}),
      ...(sex === 'female' && pregnant !== undefined ? { isPregnant: pregnant } : {}),
      ...(conditions.length > 0 ? { conditions } : {}),
      ...(medications.length > 0 ? { medications } : {}),
    };

    try {
      const { profile: updated } = await saveProfile(payload);
      setProfile(updated);
      setEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') { e.preventDefault(); action(); }
  };

  if (loading) return <div className="settings-section"><h2>Your Profile</h2><p>Loading profile...</p></div>;

  // ── Read-only view ──
  if (!editing) {
    return (
      <div className="settings-section">
        <div className="profile-header-row">
          <h2>Your Profile</h2>
          <button className="edit-button" onClick={startEditing}>Edit</button>
        </div>
        {success && <div className="profile-success">Profile updated successfully.</div>}
        {profile ? (
          <div className="profile-grid">
            <div className="profile-item"><span className="label">Date of Birth</span><span>{profile.dateOfBirth}</span></div>
            <div className="profile-item"><span className="label">Biological Sex</span><span>{sexLabel(profile.biologicalSex)}</span></div>
            <div className="profile-item"><span className="label">Height</span><span>{profile.heightCm} cm</span></div>
            <div className="profile-item"><span className="label">Weight</span><span>{profile.weightKg} kg</span></div>
            {profile.ethnicity && <div className="profile-item"><span className="label">Ethnicity</span><span>{profile.ethnicity}</span></div>}
            {profile.biologicalSex === 'female' && profile.isPregnant !== undefined && (
              <div className="profile-item"><span className="label">Pregnant</span><span>{profile.isPregnant ? 'Yes' : 'No'}</span></div>
            )}
            {profile.conditions && profile.conditions.length > 0 && (
              <div className="profile-item full"><span className="label">Conditions</span><span>{profile.conditions.join(', ')}</span></div>
            )}
            {profile.medications && profile.medications.length > 0 && (
              <div className="profile-item full"><span className="label">Medications</span><span>{profile.medications.join(', ')}</span></div>
            )}
          </div>
        ) : (
          <p>No profile found. Click Edit to set up your profile.</p>
        )}
      </div>
    );
  }

  // ── Edit view ──
  return (
    <div className="settings-section">
      <div className="profile-header-row">
        <h2>Edit Profile</h2>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="profile-edit-form">
        <div className="form-group">
          <label>Date of Birth</label>
          <input type="date" className="form-input" value={dob} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().split('T')[0]} />
        </div>

        <div className="form-group">
          <label>Biological Sex</label>
          <div className="option-group">
            {(['male', 'female', 'prefer_not_to_say'] as const).map((opt) => (
              <button key={opt} type="button" className={`option-btn ${sex === opt ? 'selected' : ''}`} onClick={() => setSex(opt)}>
                {sexLabel(opt)}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Height</label>
          <div className="measurement-row">
            {heightUnit === 'cm' ? (
              <input type="number" className="form-input" placeholder="cm" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} min="0" />
            ) : (
              <div className="imperial-fields">
                <input type="number" className="form-input" placeholder="Feet" value={heightFeet} onChange={(e) => setHeightFeet(e.target.value)} min="0" />
                <input type="number" className="form-input" placeholder="Inches" value={heightInches} onChange={(e) => setHeightInches(e.target.value)} min="0" max="11" />
              </div>
            )}
            <select className="form-select" value={heightUnit} onChange={(e) => setHeightUnit(e.target.value as 'cm' | 'feet')}>
              <option value="cm">cm</option>
              <option value="feet">ft / in</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Weight</label>
          <div className="measurement-row">
            {weightUnit === 'kg' ? (
              <input type="number" className="form-input" placeholder="kg" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} min="0" />
            ) : weightUnit === 'stone' ? (
              <div className="imperial-fields">
                <input type="number" className="form-input" placeholder="Stone" value={weightStone} onChange={(e) => setWeightStone(e.target.value)} min="0" />
                <input type="number" className="form-input" placeholder="Lbs" value={weightLbs} onChange={(e) => setWeightLbs(e.target.value)} min="0" />
              </div>
            ) : (
              <input type="number" className="form-input" placeholder="lbs" value={weightLbs} onChange={(e) => setWeightLbs(e.target.value)} min="0" />
            )}
            <select className="form-select" value={weightUnit} onChange={(e) => setWeightUnit(e.target.value as 'kg' | 'stone' | 'lbs')}>
              <option value="kg">kg</option>
              <option value="stone">st / lbs</option>
              <option value="lbs">lbs</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Ethnicity <span className="hint">(optional)</span></label>
          <select className="form-select" value={ethnicity} onChange={(e) => setEthnicity(e.target.value)}>
            <option value="">Select...</option>
            {ETHNICITY_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>

        {sex === 'female' && (
          <div className="form-group">
            <label>Currently pregnant?</label>
            <div className="option-group">
              <button type="button" className={`option-btn ${pregnant === true ? 'selected' : ''}`} onClick={() => setPregnant(true)}>Yes</button>
              <button type="button" className={`option-btn ${pregnant === false ? 'selected' : ''}`} onClick={() => setPregnant(false)}>No</button>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Conditions <span className="hint">(optional)</span></label>
          <div className="tags-input-wrapper">
            <input type="text" className="form-input" placeholder="Type a condition..." value={condInput} onChange={(e) => setCondInput(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, () => addTag(conditions, setConditions, condInput, setCondInput))} />
            <button type="button" className="add-tag-btn" onClick={() => addTag(conditions, setConditions, condInput, setCondInput)}>Add</button>
          </div>
          {conditions.length > 0 && (
            <div className="tags-list">
              {conditions.map((c, i) => (
                <span key={i} className="tag">{c}<button type="button" onClick={() => removeTag(conditions, setConditions, i)}>&times;</button></span>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Medications <span className="hint">(optional)</span></label>
          <div className="tags-input-wrapper">
            <input type="text" className="form-input" placeholder="Type a medication..." value={medInput} onChange={(e) => setMedInput(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, () => addTag(medications, setMedications, medInput, setMedInput))} />
            <button type="button" className="add-tag-btn" onClick={() => addTag(medications, setMedications, medInput, setMedInput)}>Add</button>
          </div>
          {medications.length > 0 && (
            <div className="tags-list">
              {medications.map((m, i) => (
                <span key={i} className="tag">{m}<button type="button" onClick={() => removeTag(medications, setMedications, i)}>&times;</button></span>
              ))}
            </div>
          )}
        </div>

        <div className="profile-edit-actions">
          <button className="cancel-button" onClick={cancelEditing} disabled={saving}>Cancel</button>
          <button className="save-button" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Settings ────────────────────────────────────────────────
const Settings: React.FC = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const auth = getAuth();

  const handleCancelSubscription = async () => {
    if (!subscription || !auth.currentUser) return;
    setCancelLoading(true);
    setCancelError(null);
    try {
      await cancelSubscription(subscription.id);
      setSubscription({ ...subscription, status: 'canceled', cancel_at_period_end: true });
      setShowCancelModal(false);
    } catch (err: any) {
      setCancelError(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!auth.currentUser) return;
      setLoading(true);
      setError(null);
      try {
        const response = await getSubscriptionInfo();
        if (response.subscriptions && response.subscriptions.length > 0) {
          setSubscription(response.subscriptions[0]);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load subscription information');
      } finally {
        setLoading(false);
      }
    };
    fetchSubscription();
  }, [auth.currentUser]);

  return (
    <div className="settings-container">
      <h1>Settings</h1>

      <ProfileSection />

      <div className="settings-section" id="subscription-section">
        <h2>Current Subscription</h2>
        {loading ? (
          <p>Loading subscription information...</p>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : subscription ? (
          <div className="subscription-info">
            <div className="subscription-header">
              <div className="subscription-status">
                <span className="label">Status:</span>
                <span className={`status-badge ${subscription.cancel_at ? 'canceled' : subscription.status}`}>
                  {subscription.cancel_at
                    ? 'Cancelled'
                    : subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </div>
              {(subscription.status === 'active' || subscription.status === 'trialing') && !subscription.cancel_at && (
                <button className="cancel-subscription-btn" onClick={() => setShowCancelModal(true)}>
                  Cancel
                </button>
              )}
            </div>

            {subscription.plan && (
              <>
                <div className="subscription-price">
                  <span className="label">Price:</span>
                  <span>
                    {formatPrice(subscription.plan.amount, subscription.plan.currency)}
                    {subscription.plan.interval && ` / ${subscription.plan.interval}`}
                  </span>
                </div>
                {subscription.current_period_end && (
                  <div className="subscription-period">
                    <span className="label">Current period ends:</span>
                    <span>{formatDate(subscription.current_period_end)}</span>
                  </div>
                )}
                {!!subscription.cancel_at && (
                  <div className="subscription-expiry">
                    <span className="label">Expires:</span>
                    <span>{formatDate(subscription.cancel_at)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <p>No active subscriptions found. <a href="/pricing">View our pricing plans</a>.</p>
        )}

        {showCancelModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Are you sure?</h3>
              <p>We're sad to see you go!</p>
              {cancelError && <div className="error-message">{cancelError}</div>}
              <div className="modal-actions">
                <button className="modal-cancel-btn" onClick={() => setShowCancelModal(false)} disabled={cancelLoading}>
                  No, Keep it
                </button>
                <button className="modal-confirm-btn" onClick={handleCancelSubscription} disabled={cancelLoading}>
                  {cancelLoading ? 'Processing...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="logout-container">
        <LogoutButton />
      </div>
    </div>
  );
};

// ── Logout ───────────────────────────────────────────────────────
const LogoutButton: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const handleLogout = async () => {
    setLoading(true);
    try {
      await signOut(getAuth());
      navigate('/auth', { replace: true });
    } catch {
      setLoading(false);
      alert('Error signing out. Please try again.');
    }
  };
  return (
    <button className="logout-btn" onClick={handleLogout} disabled={loading}>
      {loading ? 'Signing out...' : 'Log out'}
    </button>
  );
};

export default Settings;
